import React from 'react'
import Turnstile from './Turnstile'
import { registerClientProfile } from '../api'
import { auth } from '../firebase'
import { detectTimeZone } from '../countryRules'

type FormState = {
  firstName: string
  lastName: string
  phone: string
  email: string
}

type Props = {
  initial?: Partial<FormState>
  mode?: 'create' | 'edit'
  onSuccess?: () => void
}

const defaultState: FormState = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

export default function ClientProfileForm({ initial, mode = 'create', onSuccess }: Props) {
  const user = auth.currentUser
  const [form, setForm] = React.useState<FormState>(() => {
    const base = {
      ...defaultState,
      ...initial,
    }
    return {
      ...base,
      email: initial?.email || user?.email || base.email || '',
    }
  })
  const [submitting, setSubmitting] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = React.useState('')
  const [showTurnstile, setShowTurnstile] = React.useState(false)
  const [waitingTurnstile, setWaitingTurnstile] = React.useState(false)

  React.useEffect(() => {
    setForm(prev => ({
      ...prev,
      firstName: prev.firstName || user?.displayName?.split(' ')?.[0] || '',
      lastName:
        prev.lastName ||
        (user?.displayName && user.displayName.split(' ').slice(1).join(' ')) ||
        '',
      email: user?.email || prev.email || '',
    }))
  }, [user?.displayName, user?.email])

  const onChange =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }))
    }

  const registrationEmail = user?.email || form.email

  const valid =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.phone.trim() &&
    registrationEmail

  const submitForm = async () => {
    if (!valid || submitting) {
      setErr('Please fill all required fields.')
      return
    }
    if (!turnstileToken) {
      setShowTurnstile(true)
      setWaitingTurnstile(true)
      return
    }
    setSubmitting(true)
    setErr(null)
    try {
      await registerClientProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: registrationEmail || '',
        timeZone: detectTimeZone(),
        turnstileToken,
      })
      onSuccess?.()
    } catch (error: any) {
      setErr(error?.message || 'Failed to save profile')
      setTurnstileToken('')
    } finally {
      setSubmitting(false)
      setWaitingTurnstile(false)
      setShowTurnstile(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitForm()
  }

  React.useEffect(() => {
    if (waitingTurnstile && turnstileToken && !submitting) {
      submitForm()
    }
  }, [waitingTurnstile, turnstileToken, submitting])

  return (
    <form className="stack-3" onSubmit={handleSubmit}>
      {err && <div className="banner error">{err}</div>}

      <div className="row">
        <div className="col">
          <label className="field-label">
            First name <span style={{ color: '#dc2626' }}>*</span>
            <input className="input" value={form.firstName} onChange={onChange('firstName')} />
          </label>
        </div>
        <div className="col">
          <label className="field-label">
            Last name <span style={{ color: '#dc2626' }}>*</span>
            <input className="input" value={form.lastName} onChange={onChange('lastName')} />
          </label>
        </div>
      </div>

      <label className="field-label">
        Mobile number <span style={{ color: '#dc2626' }}>*</span>
        <input className="input" value={form.phone} onChange={onChange('phone')} />
      </label>

      <label className="field-label">
        Email <span style={{ color: '#dc2626' }}>*</span>
        <input className="input" value={form.email} onChange={onChange('email')} disabled />
      </label>

      {import.meta.env.VITE_TURNSTILE_SITE_KEY && showTurnstile && (
        <div className="field" style={{ marginTop: 8 }}>
          <div className="field-label">Quick verification</div>
          <Turnstile
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
            action="register_client"
            onToken={(token) => {
              setTurnstileToken(token)
              if (token) {
                setShowTurnstile(false)
              }
              if (!token) {
                setWaitingTurnstile(false)
                setSubmitting(false)
              }
            }}
          />
        </div>
      )}

      <button className="btn btn-primary" type="submit" disabled={submitting || waitingTurnstile}>
        {submitting ? 'Saving…' : waitingTurnstile ? 'Verifying…' : mode === 'edit' ? 'Save changes' : 'Continue'}
      </button>
    </form>
  )
}
